/**
 * Shuttle Currency Manager
 * 
 * Handles currency conversion, exchange rates, and price formatting
 * for the shuttle booking system
 * 
 * @package A2A_Theme
 */

class ShuttleCurrencyManager {
    constructor() {
        this.currentCurrency = localStorage.getItem('selectedCurrency') || 'EUR';
        this.currentSymbol = localStorage.getItem('selectedCurrencySymbol') || '€';
        this.exchangeRates = {};
        this.baseCurrency = 'EUR'; // API returns prices in EUR
        this.ratesCacheKey = 'shuttle_exchange_rates';
        this.ratesCacheExpiry = 'shuttle_rates_expiry';
        this.cacheTimeout = 60 * 60 * 1000; // 1 hour in milliseconds
        
        // WordPress AJAX URL for currency rates
        this.ajaxUrl = (typeof ajaxurl !== 'undefined') ? ajaxurl : '/wp-admin/admin-ajax.php';
            
        
        // Load exchange rates on initialization
        this.loadExchangeRates();
    }
    
    /**
     * Load exchange rates from API or cache
     */
    async loadExchangeRates() {
        try {
            // Check if we have cached rates that are still valid
            const cachedRates = this.getCachedRates();
            if (cachedRates) {
                this.exchangeRates = cachedRates;
                return;
            }
            
            // Fetch fresh rates from WordPress AJAX endpoint
            const formData = new FormData();
            formData.append('action', 'get_currency_rates');
            
            const response = await fetch(this.ajaxUrl, {
                method: 'POST',
                body: formData
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const ajaxResponse = await response.json();
            
            if (!ajaxResponse.success) {
                throw new Error(`AJAX error: ${ajaxResponse.data?.message || 'Unknown error'}`);
            }
            
            const rates = ajaxResponse.data.rates;
            
            // Process and cache the rates
            this.exchangeRates = this.processExchangeRates(rates);
            this.cacheExchangeRates(this.exchangeRates);
            
        } catch (error) {
            console.error('Failed to load exchange rates:', error);
            // Use fallback rates or continue with EUR only
            this.exchangeRates = {};
            
            // Dispatch error event for UI handling
            window.dispatchEvent(new CustomEvent('shuttleCurrencyError', {
                detail: { 
                    error: 'Failed to load exchange rates',
                    fallback: 'Using EUR prices only',
                    timestamp: Date.now()
                }
            }));
        }
    }
    
    /**
     * Process raw exchange rates from API
     */
    processExchangeRates(rawRates) {
        const processedRates = {};
        
        // API returns rates like: {"EUR-GBP": 0.9112, "EUR-USD": 1.2236}
        Object.entries(rawRates).forEach(([pair, rate]) => {
            const [from, to] = pair.split('-');
            
            // Store direct conversion rates
            if (!processedRates[from]) processedRates[from] = {};
            processedRates[from][to] = rate;
            
            // Store reverse conversion rates
            if (!processedRates[to]) processedRates[to] = {};
            processedRates[to][from] = 1 / rate;
        });
        
        // Ensure EUR to EUR rate exists
        if (!processedRates.EUR) processedRates.EUR = {};
        processedRates.EUR.EUR = 1;
        
        return processedRates;
    }
    
    /**
     * Get cached exchange rates if still valid
     */
    getCachedRates() {
        try {
            const cachedRates = localStorage.getItem(this.ratesCacheKey);
            const cacheExpiry = localStorage.getItem(this.ratesCacheExpiry);
            
            if (cachedRates && cacheExpiry) {
                const expiryTime = parseInt(cacheExpiry);
                if (Date.now() < expiryTime) {
                    return JSON.parse(cachedRates);
                }
            }
        } catch (error) {
            console.error('Error reading cached rates:', error);
        }
        return null;
    }
    
    /**
     * Cache exchange rates with expiry
     */
    cacheExchangeRates(rates) {
        try {
            const expiryTime = Date.now() + this.cacheTimeout;
            localStorage.setItem(this.ratesCacheKey, JSON.stringify(rates));
            localStorage.setItem(this.ratesCacheExpiry, expiryTime.toString());
        } catch (error) {
            console.error('Error caching exchange rates:', error);
        }
    }
    
    /**
     * Convert price from one currency to another
     */
    convertPrice(amount, fromCurrency = 'EUR', toCurrency = null) {
        toCurrency = toCurrency || this.currentCurrency;
        
        // No conversion needed if currencies are the same
        if (fromCurrency === toCurrency) {
            return amount;
        }
        
        try {
            // Get conversion rate
            const rate = this.getConversionRate(fromCurrency, toCurrency);
            const convertedAmount = amount * rate;
            return convertedAmount;
            
        } catch (error) {
            console.error('Currency conversion failed:', error);
            // Return original amount as fallback
            return amount;
        }
    }
    
    /**
     * Get conversion rate between two currencies
     */
    getConversionRate(fromCurrency, toCurrency) {
        // Direct conversion
        if (this.exchangeRates[fromCurrency] && this.exchangeRates[fromCurrency][toCurrency]) {
            return this.exchangeRates[fromCurrency][toCurrency];
        }
        
        // Cross-conversion through EUR
        if (fromCurrency !== 'EUR' && toCurrency !== 'EUR') {
            const toEurRate = this.exchangeRates[fromCurrency]?.EUR;
            const fromEurRate = this.exchangeRates.EUR?.[toCurrency];
            
            if (toEurRate && fromEurRate) {
                return toEurRate * fromEurRate;
            }
        }
        
        // Fallback: no conversion (1:1 rate)
        console.warn(`No conversion rate found for ${fromCurrency} to ${toCurrency}, using 1:1`);
        return 1;
    }
    
    /**
     * Format price with correct symbol and decimal places
     */
    formatPrice(amount, currency = null, symbol = null) {
        currency = currency || this.currentCurrency;
        symbol = symbol || this.currentSymbol;
        
        // Round to 2 decimal places
        const roundedAmount = Math.round(amount * 100) / 100;
        
        // Format based on currency
        switch (currency) {
            case 'EUR':
                return `€${roundedAmount.toFixed(2)}`;
            case 'GBP':
                return `£${roundedAmount.toFixed(2)}`;
            case 'USD':
                return `$${roundedAmount.toFixed(2)}`;
            case 'CHF':
                return `CHF ${roundedAmount.toFixed(2)}`;
            case 'PLN':
                return `${roundedAmount.toFixed(2)} zł`;
            case 'CZK':
                return `${roundedAmount.toFixed(0)} Kč`; // Czech koruna typically no decimals
            default:
                return `${symbol}${roundedAmount.toFixed(2)}`;
        }
    }
    
    /**
     * Update current currency and symbol
     */
    async updateCurrency(currencyCode, currencySymbol) {
        this.currentCurrency = currencyCode;
        this.currentSymbol = currencySymbol;
        
        // Update localStorage
        localStorage.setItem('selectedCurrency', currencyCode);
        localStorage.setItem('selectedCurrencySymbol', currencySymbol);
        
        // Ensure we have exchange rates for the new currency
        if (!this.exchangeRates[currencyCode] && currencyCode !== 'EUR') {
            await this.loadExchangeRates();
        }
    }
    
    /**
     * Get currency symbol for a given currency code
     */
    getCurrencySymbol(currencyCode) {
        const symbols = {
            'EUR': '€',
            'GBP': '£',
            'USD': '$',
            'CHF': 'CHF ',
            'PLN': 'zł',
            'CZK': 'Kč'
        };
        return symbols[currencyCode] || currencyCode + ' ';
    }
    
    /**
     * Check if currency conversion is available
     */
    isConversionAvailable(fromCurrency, toCurrency) {
        if (fromCurrency === toCurrency) return true;
        
        return !!(this.exchangeRates[fromCurrency] && 
                 this.exchangeRates[fromCurrency][toCurrency]) ||
               !!(this.exchangeRates[fromCurrency]?.EUR && 
                 this.exchangeRates.EUR?.[toCurrency]);
    }
    
    /**
     * Get current currency info
     */
    getCurrentCurrency() {
        return {
            code: this.currentCurrency,
            symbol: this.currentSymbol
        };
    }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ShuttleCurrencyManager;
}
